<template>
  <div
    class="custom-select"
    :class="{
      'is-disabled': disabled,
      'is-focus': isOpen,
      [`custom-select--${size}`]: size,
    }"
    @click="toggleDropdown"
    @keydown.enter.prevent="toggleDropdown"
    @keydown.space.prevent="toggleDropdown"
    @keydown.up.prevent="navigateOptions(-1)"
    @keydown.down.prevent="navigateOptions(1)"
    @keydown.esc="closeDropdown"
    tabindex="0"
    role="combobox"
    :aria-expanded="isOpen"
    aria-haspopup="listbox"
    :aria-labelledby="id + '-label'"
    :aria-describedby="id + '-description'"
  >
    <!-- 内容容器 -->
    <div class="custom-select__content">
      <!-- 选中项显示内容 - 支持插槽自定义 -->
      <div class="custom-select__selected" v-if="selectedOption">
        <slot
          name="default"
          :option="selectedOption"
          :selected="true"
          :index="selectedIndex"
        >
          <!-- 默认显示：前置图片 + 文本 -->
          <img
            v-if="selectedOption[iconField] && showIcon"
            :src="selectedOption[iconField]"
            :alt="selectedOption[labelField]"
            class="custom-select__icon"
            :style="iconStyle"
          />
          <span class="custom-select__label">{{
            selectedOption[labelField]
          }}</span>
        </slot>
      </div>
      <div class="custom-select__placeholder" v-else>
        <div class="tw-w-full tw-h-full tw-relative">
          <input
            type="text"
            :required="required"
            :aria-required="required"
            class="tw-w-1 tw-absolute tw-z-0"
          />
          <slot name="placeholder">
            <div
              class="tw-h-full tw-flex tw-items-center tw-absolute tw-z-10 tw-top-0"
            >
              {{ placeholder || $t("请选择") }}
            </div>
          </slot>
        </div>
      </div>
    </div>

    <!-- 清除按钮 -->
    <i
      v-if="clearable && !disabled && selectedOption"
      class="custom-select__clear"
      role="button"
          :aria-label="$t('清除选择')"
      tabindex="0"
      @click.stop="clearSelection"
      @keydown.enter.stop.prevent="clearSelection"
    >
      <svg viewBox="0 0 1024 1024" width="14" height="14" aria-hidden="true">
        <path
          d="M563.2 512l204.8-204.8a35.2 35.2 0 0 0-49.92-49.92L512 462.08 307.2 257.28a35.2 35.2 0 1 0-49.92 49.92L462.08 512l-204.8 204.8a35.2 35.2 0 1 0 49.92 49.92L512 561.92l204.8 204.8a35.2 35.2 0 0 0 49.92-49.92z"
          fill="currentColor"
        ></path>
      </svg>
    </i>

    <!-- 下拉选项列表 -->
    <transition name="el-zoom-in-top">
      <div
        v-show="isOpen"
        ref="dropdown"
        class="custom-select__dropdown"
        :style="dropdownStyle"
      >
        <ul
          class="custom-select__menu"
          role="listbox"
          :aria-labelledby="id + '-label'"
          :aria-activedescendant="focusedOptionId"
        >
          <li
            v-for="(option, index) in options"
            :key="`${index}-'op'` || option[valueField]"
            :id="`${id}-option-${index}`"
            class="custom-select__option"
            :class="{
              'is-selected': isSelected(option),
              'is-focused': focusedIndex === index,
              'is-disabled': option.disabled,
            }"
            role="option"
            :aria-selected="isSelected(option)"
            @click.stop="selectOption(option)"
            @mouseenter="focusedIndex = index"
          >
            <!-- 选项内容 - 支持插槽自定义 -->
            <slot
              name="option"
              :option="option"
              :index="index"
              :selected="isSelected(option)"
              :focused="focusedIndex === index"
            >
              <!-- 默认显示：选项图片 + 文本 + 选中标记 -->
              <img
                v-if="option[iconField] && showIcon"
                :src="option[iconField]"
                :alt="option[labelField]"
                class="custom-select__option-icon"
                :style="iconStyle"
              />
              <span class="custom-select__option-label">{{
                option[labelField]
              }}</span>
              <i v-if="isSelected(option)" class="custom-select__check">
                <svg viewBox="0 0 1024 1024" width="16" height="16">
                  <path
                    d="M406.656 706.944L195.84 496.256a32 32 0 1 0-45.248 45.248l256 256 512-512a32 32 0 0 0-45.248-45.248L406.656 706.944z"
                    fill="currentColor"
                  ></path>
                </svg>
              </i>
            </slot>
          </li>
          <li v-if="!options.length" class="custom-select__empty">
            <slot name="empty">
              {{ emptyText || $t("无数据") }}
            </slot>
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script>
/**
 * CustomSelect 自定义选择器组件
 *
 * 基于 ul/li 实现的下拉选择组件，支持丰富的插槽自定义
 *
 * 插槽说明：
 * - default: 自定义选中项显示内容，作用域参数：{ option, selected, index }
 * - placeholder: 自定义占位符内容
 * - option: 自定义选项显示内容，作用域参数：{ option, index, selected, focused }
 * - empty: 自定义空数据显示内容
 *
 * 使用示例：
 *
 * <!-- 基础用法 -->
 * <custom-select v-model="value" :options="options" />
 *
 * <!-- 自定义选中项显示 -->
 * <custom-select v-model="value" :options="options">
 *   <template #default="{ option }">
 *     <i class="icon" :class="option.icon"></i>
 *     <span>{{ option.name }}</span>
 *     <span class="badge">{{ option.count }}</span>
 *   </template>
 * </custom-select>
 *
 * <!-- 自定义选项内容 -->
 * <custom-select v-model="value" :options="options">
 *   <template #option="{ option, selected }">
 *     <div class="flex items-center justify-between w-full">
 *       <span>{{ option.name }}</span>
 *       <span v-if="selected" class="checkmark">✓</span>
 *     </div>
 *   </template>
 * </custom-select>
 */
export default {
  name: "CustomSelect",
  model: {
    prop: "value",
    event: "change",
  },
  props: {
    // 当前值
    value: {
      type: [String, Number, Boolean, Object],
      default: null,
    },
    required: {
      type: Boolean,
      default: false,
    },
    // 选项数据
    options: {
      type: Array,
      required: true,
      default: () => [],
    },
    // 值字段名
    valueField: {
      type: String,
      default: "value",
    },
    // 标签字段名
    labelField: {
      type: String,
      default: "text",
    },
    // 图标字段名
    iconField: {
      type: String,
      default: "icon",
    },
    // 占位符
    placeholder: {
      type: String,
      default: "",
    },
    // 是否禁用
    disabled: {
      type: Boolean,
      default: false,
    },
    // 尺寸
    size: {
      type: String,
      default: "default",
      validator: (value) => ["lg", "default", "sm"].includes(value),
    },
    // 是否显示图标
    showIcon: {
      type: Boolean,
      default: true,
    },
    // 图标样式
    iconStyle: {
      type: Object,
      default: () => ({
        width: "16px",
        height: "16px",
        borderRadius: "2px",
      }),
    },
    // 无数据文本
    emptyText: {
      type: String,
      default: "",
    },
    // 组件ID
    id: {
      type: String,
      default: () => `custom-select-${Math.random().toString(36).substr(2, 9)}`,
    },
    // 是否可清空
    clearable: {
      type: Boolean,
      default: false,
    },
    // 下拉列表是否固定位置
    fixedDropdown: {
      type: Boolean,
      default: false,
    },
    dropdownZIndex: {
      type: Number,
      default: 3000,
    },
  },
  data() {
    return {
      isOpen: false,
      focusedIndex: -1,
      dropdownPosition: { top: 0, left: 0, width: 0 },
      _dropdownMoved: false,
      _dropdownPlaceholder: null,
      _dropdownOriginalParent: null,
    };
  },
  computed: {
    selectedOption() {
      if (this.value === null || this.value === undefined) return null;
      // 当 v-model 传入的是对象时，优先使用 valueField 进行匹配；否则回退到引用相等
      if (this.value && typeof this.value === "object") {
        if (Object.prototype.hasOwnProperty.call(this.value, this.valueField)) {
          const target = this.value[this.valueField];
          return (
            this.options.find((option) => option[this.valueField] === target) ||
            null
          );
        }
        const found = this.options.find((option) => option === this.value);
        return found || null;
      }
      // 基础类型按原逻辑匹配
      return (
        this.options.find((option) => option[this.valueField] === this.value) ||
        null
      );
    },
    selectedIndex() {
      if (!this.options || !this.options.length) return -1;
      if (this.value === null || this.value === undefined) return -1;
      return this.options.findIndex((opt) => this.isSelected(opt));
    },
    focusedOptionId() {
      return this.focusedIndex >= 0
        ? `${this.id}-option-${this.focusedIndex}`
        : "";
    },
    dropdownStyle() {
      const style = {};
      if (this.fixedDropdown && this.isOpen) {
        style.position = "fixed";
        style.top = this.dropdownPosition.top + "px";
        style.left = this.dropdownPosition.left + "px";
        style.minWidth = this.dropdownPosition.width + "px";
        style.zIndex = this.dropdownZIndex;
        style.margin = "0"; // 避免任何 margin 影响视觉对齐
      } else {
        style.position = "absolute";
        style.top = "100%";
        style.left = "0";
        style.minWidth = "100%";
        style.zIndex = this.dropdownZIndex;
        // 其余视觉样式走类名
      }
      return style;
    },
  },
  methods: {
    toggleDropdown() {
      if (this.disabled) return;
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.focusedIndex = this.selectedIndex >= 0 ? this.selectedIndex : 0;
        if (this.fixedDropdown) {
          this.appendDropdownToBody();
          this.updateDropdownPosition();
        }
        this.$nextTick(() => {
          this.scrollToFocused();
        });
      } else {
        if (this.fixedDropdown) {
          this.removeDropdownFromBody();
        }
      }
    },
    updateDropdownPosition() {
      if (!this.fixedDropdown) return;
      const rect = this.$el.getBoundingClientRect();
      this.dropdownPosition = {
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      };
    },
    appendDropdownToBody() {
      const el = this.$refs.dropdown;
      if (!el || this._dropdownMoved) return;
      this._dropdownOriginalParent = el.parentNode;
      this._dropdownPlaceholder = document.createComment(
        "custom-select-dropdown-placeholder"
      );
      this._dropdownOriginalParent.insertBefore(this._dropdownPlaceholder, el);
      document.body.appendChild(el);
      this._dropdownMoved = true;
    },
    removeDropdownFromBody() {
      const el = this.$refs.dropdown;
      if (!el || !this._dropdownMoved) return;
      if (this._dropdownPlaceholder && this._dropdownOriginalParent) {
        this._dropdownOriginalParent.insertBefore(
          el,
          this._dropdownPlaceholder
        );
        this._dropdownOriginalParent.removeChild(this._dropdownPlaceholder);
      }
      this._dropdownMoved = false;
      this._dropdownPlaceholder = null;
      this._dropdownOriginalParent = null;
    },
    closeDropdown() {
      this.isOpen = false;
      this.focusedIndex = -1;
      if (this.fixedDropdown) {
        this.removeDropdownFromBody();
      }
    },
    // 清空选择
    clearSelection() {
      this.$emit("change", null);
      this.$emit("clear");
      this.closeDropdown();
    },
    selectOption(option) {
      if (option.disabled) return;
      const emittedValue =
        this.value && typeof this.value === "object"
          ? option
          : option[this.valueField];
      this.$emit("change", emittedValue);
      this.$emit("select", option);
      this.closeDropdown();
    },
    isSelected(option) {
      if (this.value === null || this.value === undefined) return false;
      if (this.value && typeof this.value === "object") {
        if (Object.prototype.hasOwnProperty.call(this.value, this.valueField)) {
          return option[this.valueField] === this.value[this.valueField];
        }
        return option === this.value;
      }
      return option[this.valueField] === this.value;
    },
    navigateOptions(direction) {
      if (!this.isOpen) {
        this.toggleDropdown();
        return;
      }

      const optionsLength = this.options.length;
      if (optionsLength === 0) return;

      let newIndex = this.focusedIndex + direction;

      // 循环导航
      if (newIndex < 0) {
        newIndex = optionsLength - 1;
      } else if (newIndex >= optionsLength) {
        newIndex = 0;
      }

      // 跳过禁用选项
      while (this.options[newIndex]?.disabled) {
        newIndex += direction;
        if (newIndex < 0) newIndex = optionsLength - 1;
        if (newIndex >= optionsLength) newIndex = 0;

        // 防止无限循环
        if (newIndex === this.focusedIndex) break;
      }

      this.focusedIndex = newIndex;
      this.$nextTick(() => {
        this.scrollToFocused();
      });
    },
    scrollToFocused() {
      const menu = this.$el.querySelector(".custom-select__menu");
      const focusedOption = this.$el.querySelector(
        ".custom-select__option.is-focused"
      );

      if (menu && focusedOption) {
        const menuRect = menu.getBoundingClientRect();
        const optionRect = focusedOption.getBoundingClientRect();

        if (optionRect.bottom > menuRect.bottom) {
          menu.scrollTop += optionRect.bottom - menuRect.bottom;
        } else if (optionRect.top < menuRect.top) {
          menu.scrollTop -= menuRect.top - optionRect.top;
        }
      }
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.closeDropdown();
      }
    },
    updateDropdownPosition() {
      // 非 fixed 模式下无需计算
      if (!this.fixedDropdown) return;
      const rect = this.$el.getBoundingClientRect();
      this.dropdownPosition = {
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      };
    },
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
    this._onWindowEvents = () => {
      if (!this.isOpen) return;
      this.updateDropdownPosition();
    };
    window.addEventListener("scroll", this._onWindowEvents, true);
    window.addEventListener("resize", this._onWindowEvents);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
    window.removeEventListener("scroll", this._onWindowEvents, true);
    window.removeEventListener("resize", this._onWindowEvents);
    if (this.fixedDropdown) {
      this.removeDropdownFromBody();
    }
  },
};
</script>

<style lang="scss" scoped>
.custom-select {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  width: 100%;
  padding: 0.375rem 1.75rem 0.375rem 0.75rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #fff;
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);

  &:hover:not(.is-disabled) {
    border-color: #c0c4cc;
  }

  &.is-focus {
    border-color: #409eff;
  }

  &.is-disabled {
    background-color: #f5f7fa;
    border-color: #e4e7ed;
    color: #c0c4cc;
    cursor: not-allowed;
  }

  // 尺寸变体
  &--lg {
    padding: 0.5rem 0 0.5rem 1rem;
    font-size: 1.25rem;
  }

  &--sm {
    height: 31px;
    padding: 0.25rem 0 0.25rem 0.5rem;
    font-size: 0.875rem;
  }
}

.custom-select__content {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  margin-right: 8px;
  height: 100%;
}

.custom-select__selected,
.custom-select__placeholder {
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
}

.custom-select__placeholder {
  color: #c0c4cc;
}

.custom-select__icon {
  margin-right: 8px;
  flex-shrink: 0;
}

.custom-select__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.custom-select__dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  margin: 5px 0;
  min-width: 100%;
}

.custom-select__menu {
  list-style: none;
  padding: 6px 0;
  margin: 0;
  max-height: 274px;
  overflow-y: auto;
}

.custom-select__option {
  display: flex;
  align-items: center;
  padding: 0 20px;
  position: relative;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #606266;
  height: 34px;
  line-height: 34px;
  box-sizing: border-box;
  cursor: pointer;

  &:hover:not(.is-disabled) {
    background-color: #f5f7fa;
  }

  &.is-selected {
    color: #409eff;
    font-weight: bold;
  }

  &.is-focused {
    background-color: #f5f7fa;
  }

  &.is-disabled {
    color: #c0c4cc;
    cursor: not-allowed;
  }
}

.custom-select__option-icon {
  margin-right: 8px;
  flex-shrink: 0;
}

.custom-select__option-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

.custom-select__check {
  margin-left: 8px;
  color: #409eff;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
}

.custom-select__empty {
  padding: 10px 0;
  margin: 0;
  text-align: center;
  color: #999;
  font-size: 14px;
  list-style: none;
}

// 动画
.el-zoom-in-top-enter-active,
.el-zoom-in-top-leave-active {
  opacity: 1;
  transform: scaleY(1);
  transition: transform 300ms cubic-bezier(0.23, 1, 0.32, 1),
    opacity 300ms cubic-bezier(0.23, 1, 0.32, 1);
  transform-origin: center top;
}

.el-zoom-in-top-enter,
.el-zoom-in-top-leave-active {
  opacity: 0;
  transform: scaleY(0);
}

/* 清除按钮样式 */
.custom-select__clear {
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  color: #c0c4cc;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  z-index: 1;
  pointer-events: auto;
}

.custom-select:hover .custom-select__clear {
  color: #909399;
}

.custom-select__clear:hover {
  color: #606266;
}

.custom-select__content {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  margin-right: 8px;
  height: 100%;
}

.custom-select__selected,
.custom-select__placeholder {
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
}

.custom-select__placeholder {
  color: #c0c4cc;
}

.custom-select__icon {
  margin-right: 8px;
  flex-shrink: 0;
}

.custom-select__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.custom-select__dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  margin: 5px 0;
  min-width: 100%;
}

.custom-select__menu {
  list-style: none;
  padding: 6px 0;
  margin: 0;
  max-height: 274px;
  overflow-y: auto;
}

.custom-select__option {
  display: flex;
  align-items: center;
  padding: 0 20px;
  position: relative;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #606266;
  height: 34px;
  line-height: 34px;
  box-sizing: border-box;
  cursor: pointer;

  &:hover:not(.is-disabled) {
    background-color: #f5f7fa;
  }

  &.is-selected {
    color: #409eff;
    font-weight: bold;
  }

  &.is-focused {
    background-color: #f5f7fa;
  }

  &.is-disabled {
    color: #c0c4cc;
    cursor: not-allowed;
  }
}

.custom-select__option-icon {
  margin-right: 8px;
  flex-shrink: 0;
}

.custom-select__option-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

.custom-select__check {
  margin-left: 8px;
  color: #409eff;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
}

.custom-select__empty {
  padding: 10px 0;
  margin: 0;
  text-align: center;
  color: #999;
  font-size: 14px;
  list-style: none;
}

// 动画
.el-zoom-in-top-enter-active,
.el-zoom-in-top-leave-active {
  opacity: 1;
  transform: scaleY(1);
  transition: transform 300ms cubic-bezier(0.23, 1, 0.32, 1),
    opacity 300ms cubic-bezier(0.23, 1, 0.32, 1);
  transform-origin: center top;
}

.el-zoom-in-top-enter,
.el-zoom-in-top-leave-active {
  opacity: 0;
  transform: scaleY(0);
}
</style>
